import zonesService from '../services/zones.service';

/**
 * ZONES — AVA-181 shared zones (api/v1/zones).
 * Palette state for the current facility (the active zones that apply there: its own and those its
 * facility group shares; selection, boundary visibility) plus thin
 * actions the admin page and assignment dialogs call. Admin lists with retired zones are not cached
 * here. Zone attributes are the fields of each zone type (`type.fields`).
 */
export const ZONES = {
  namespaced: true,
  state: {
    // The facility the cached zones belong to; a facility switch reloads them.
    facilityId: null,
    zoneTypes: [],
    zones: [],
    loading: false,
    selectedZoneId: null,
    typeFilter: null,
    // Zone boundaries on the open 2D level plan, toggled from the viewer quick menu.
    showPlanBoundaries: false,
    records: { tags: [], tasks: [] },
    recordsLoading: false,
  },
  getters: {
    GET_ZONE_TYPES: (state) => state.zoneTypes,
    GET_ZONES: (state) => state.zones,
    GET_LOADING: (state) => state.loading,
    GET_TYPE_FILTER: (state) => state.typeFilter,
    GET_SHOW_PLAN_BOUNDARIES: (state) => state.showPlanBoundaries,
    GET_SELECTED_ZONE_ID: (state) => state.selectedZoneId,
    GET_SELECTED_ZONE: (state) => state.zones.find((z) => z.id === state.selectedZoneId) || null,
    GET_RECORDS: (state) => state.records,
    GET_RECORDS_LOADING: (state) => state.recordsLoading,
    GET_FILTERED_ZONES: (state) => state.zones.filter((z) => !state.typeFilter || z.zoneTypeId === state.typeFilter),
    GET_ZONE_TYPE_NAME: (state) => (typeId) => state.zoneTypes.find((t) => t.id === typeId)?.name || '—',
    GET_ZONE_TYPE: (state) => (typeId) => state.zoneTypes.find((t) => t.id === typeId) || null,
  },
  mutations: {
    SET_FACILITY_ID(state, value) {
      state.facilityId = value;
    },
    SET_ZONE_TYPES(state, value) {
      state.zoneTypes = value;
    },
    SET_ZONES(state, value) {
      state.zones = value;
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_SELECTED_ZONE_ID(state, value) {
      state.selectedZoneId = value;
    },
    SET_TYPE_FILTER(state, value) {
      state.typeFilter = value;
    },
    SET_SHOW_PLAN_BOUNDARIES(state, value) {
      state.showPlanBoundaries = value;
    },
    SET_RECORDS(state, value) {
      state.records = value;
    },
    SET_RECORDS_LOADING(state, value) {
      state.recordsLoading = value;
    },
  },
  actions: {
    async FETCH_ALL({ commit, state, rootState }) {
      const facilityId = rootState.assetId;
      if (!facilityId) return;
      commit('SET_LOADING', true);
      try {
        const [types, zones] = await Promise.all([zonesService.getZoneTypes(), zonesService.getZones({ facilityId })]);
        if (facilityId !== rootState.assetId) return;
        if (state.facilityId !== facilityId) {
          commit('SET_SELECTED_ZONE_ID', null);
          commit('SET_TYPE_FILTER', null);
          commit('SET_RECORDS', { tags: [], tasks: [] });
        }
        commit('SET_FACILITY_ID', facilityId);
        commit('SET_ZONE_TYPES', types.data);
        commit('SET_ZONES', zones.data);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    // Loads the current facility's zones unless they are already cached.
    async ENSURE_LOADED({ state, rootState, dispatch }) {
      if (state.loading || (state.facilityId && state.facilityId === rootState.assetId)) return;
      await dispatch('FETCH_ALL');
    },
    async SELECT_ZONE({ commit, dispatch }, zoneId) {
      commit('SET_SELECTED_ZONE_ID', zoneId);
      commit('SET_RECORDS', { tags: [], tasks: [] });
      if (zoneId) await dispatch('FETCH_RECORDS');
    },
    async FETCH_RECORDS({ commit, state }) {
      if (!state.selectedZoneId) return;
      const zoneId = state.selectedZoneId;
      commit('SET_RECORDS_LOADING', true);
      try {
        const response = await zonesService.getZoneRecords(zoneId);
        if (state.selectedZoneId === zoneId) commit('SET_RECORDS', response.data);
      } finally {
        commit('SET_RECORDS_LOADING', false);
      }
    },
    // payload: items, or { items, replace } once the user chose to replace (one-zone-only types).
    async ASSIGN({ dispatch }, payload) {
      const { items, replace = false } = Array.isArray(payload) ? { items: payload } : payload;
      const response = await zonesService.assign(items, { replace });
      await Promise.all([dispatch('FETCH_RECORDS'), dispatch('REFRESH_ZONES')]);
      return response.data;
    },
    async UNASSIGN({ dispatch }, assignmentIds) {
      const response = await zonesService.unassign(assignmentIds);
      await Promise.all([dispatch('FETCH_RECORDS'), dispatch('REFRESH_ZONES')]);
      return response.data;
    },
    async REFRESH_ZONES({ commit, state }) {
      if (!state.facilityId) return;
      const [types, zones] = await Promise.all([zonesService.getZoneTypes(), zonesService.getZones({ facilityId: state.facilityId })]);
      commit('SET_ZONE_TYPES', types.data);
      commit('SET_ZONES', zones.data);
      // A zone retired elsewhere drops out of the palette selection.
      if (state.selectedZoneId && !zones.data.some((z) => z.id === state.selectedZoneId)) {
        commit('SET_SELECTED_ZONE_ID', null);
      }
    },
  },
};
